/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { isEmpty } from '@ember/utils';
import DS from 'ember-data';
import { CONDITION_TYPES, isComplete, isDuplicate } from 'cio/utils/conditions';
import { deserializeFilter as deserializeAttributeFilter } from 'cio/utils/conditions/deserialize/attribute';
import ActionCondition from 'cio/app-models/conditions/action';
import EventCondition from 'cio/app-models/conditions/event';
import SegmentModel from 'cio/models/segment';
import deserializeFilters from 'cio/utils/conditions/deserialize/filter';
import serializeFilters from 'cio/utils/conditions/serialize/filter';
import { FilterCondition, Filters } from 'cio/utils/filters';
import CollectionModel from 'cio/models/collection';
import CampaignModel from 'cio/models/campaign';
import {
  SerializedLegacyFilter,
  SerializedLegacyFilterType,
  SerializedLegacySegmentNegatableFilter,
} from 'cio/types/conditions/legacy-filter';
import AbstractCondition from 'cio/app-models/conditions/abstract';
import DeviceAttributeCondition from 'cio/app-models/conditions/device-attribute';
import { ConditionEditorSubLayout } from 'cio/utils/condition-editor';
import ShortSegmentModel from 'cio/models/short-segment';
import { ConditionViewerBackgroundVariant } from 'cio/utils/condition-viewer';
import AuthService from 'cio/services/auth';
import { tracked } from '@glimmer/tracking';
import { ConditionType } from 'cio/utils/condition-types';
import EnvironmentService from 'cio/services/environment';
import RelationshipCondition from 'cio/app-models/conditions/relationship';
import ObjectTypesService from 'cio/services/object-types';
import { ObjectAttributeSource } from 'cio/app-models/conditions/object-attribute';

type TypeButton = {
  name: string;
  icon: string;
  addButtonLabel: string;
  isDisabled?: boolean;
  disabledTooltipContent?: string;
};

export interface DropdownOption {
  label: string;
  value: SerializedLegacyFilterType;
  disabled?: boolean;
}

export enum AddStyleVariant {
  buttons = 'buttons',
  dropdown = 'dropdown',
  boxes = 'boxes', // use <ConditionList::Boxes/>, adding more condition types, requires updates to "ConditionDetailsForBoxes"
}

interface ConditionListArgs {
  conditions: SerializedLegacyFilter[] | Filters;
  conditionTypes?: ConditionType[];

  onUpdate: (conditions: SerializedLegacyFilter[] | Filters) => unknown;

  canAdd?: boolean;

  allowedConditionOperators?: string[];

  preferFilters?: boolean;

  allowAllVariationsSelection?: boolean;
  allowArrayOperators?: boolean;
  allowComparisonOperator?: boolean;
  allowForeignEvent?: boolean;
  allowInParentWildcardSelection?: boolean;
  allowMessageConditionSpecificLink?: boolean;
  allowMessageVariationSelection?: boolean;
  allowMessageWildcardSelection?: boolean;
  allowJsonOperators?: boolean;

  onToggleCreateSegment: () => unknown;

  variation?: 'collection' | 'compact' | 'segment';
  direction?: 'column';
  subLayout?: ConditionEditorSubLayout;
  addStyle?: AddStyleVariant;
  excludedNewsletterConditionId?: string | number;

  disabled?: boolean;

  readOnly?: boolean;
  readOnlyLabelClass?: string;
  readOnlyIconClass?: string;

  isCampaignReEntrant?: boolean;

  campaign?: CampaignModel;
  event?: string;
  webhook?: string;
  collection?: CollectionModel;

  dropdownPrompt?: string;

  openDropdownOnAdd?: ConditionType[];
  openDropdownOnEdit?: ConditionType[];

  showClearButton?: boolean;
  onClickClearButton?: () => unknown;

  buttonLabelOverrides?: Record<string, string>;

  autosuggestValueOverrides?: Record<string, string[] | { label: string; value: string }[]>;
  isLoadingAutosuggestValueOverrides?: boolean;

  showEventNameInEditor?: boolean;
  editContainerClass?: string;

  startInCreateState?: boolean;
  initialCreateConditionType?: ConditionType;
  conditionViewerBackgroundVariant?: ConditionViewerBackgroundVariant;

  hiddenEventInverseOptions?: string[];

  objectTypeId?: string | number;

  onNewSegmentCreated?: () => void;

  showSummary?: boolean;
  useUpdateLabel?: boolean;
  allowChangeTypeInline?: boolean;
  hideTitle?: boolean;
  preferFilterOverCondition?: boolean;
  addConditionDescription?: string;
}

type ButtonConditionType = keyof typeof CONDITION_TYPES;

export default class ConditionList extends Component<ConditionListArgs> {
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service objectTypes!: ObjectTypesService;

  AddStyleVariants = AddStyleVariant;

  get conditionTypes() {
    return this.args.conditionTypes ?? [];
  }

  get canAdd() {
    return this.args.canAdd ?? true;
  }

  get preferFilters() {
    return this.args.preferFilters ?? false;
  }

  get allowArrayOperators() {
    return this.args.allowArrayOperators ?? false;
  }

  get allowComparisonOperator() {
    return this.args.allowComparisonOperator ?? false;
  }

  get allowForeignEvent() {
    return this.args.allowForeignEvent ?? false;
  }

  get allowInParentWildcardSelection() {
    return this.args.allowInParentWildcardSelection ?? true;
  }

  get allowMessageConditionSpecificLink() {
    return this.args.allowMessageConditionSpecificLink ?? false;
  }

  get allowMessageVariationSelection() {
    return this.args.allowMessageVariationSelection ?? false;
  }

  get allowMessageWildcardSelection() {
    return this.args.allowMessageWildcardSelection ?? true;
  }

  get allowJsonOperators() {
    return this.args.allowJsonOperators ?? false;
  }

  get canCreateSegment() {
    return this.auth.hasPermission('journeys/segments/create');
  }

  get addStyle() {
    return this.args.addStyle ?? 'buttons';
  }

  get disabled() {
    if (this.disableChangesDueToRedactedUser) {
      return true;
    }
    return this.args.disabled ?? false;
  }

  get readOnly() {
    return this.args.readOnly ?? false;
  }

  get dropdownPrompt() {
    return this.args.dropdownPrompt ?? 'Add action condition';
  }

  get showClearButton() {
    return this.args.showClearButton ?? false;
  }

  get isLoadingAutosuggestValueOverrides() {
    return this.args.isLoadingAutosuggestValueOverrides ?? false;
  }

  get showEventNameInEditor() {
    return this.args.showEventNameInEditor ?? true;
  }

  get startInCreateState() {
    return this.args.startInCreateState ?? false;
  }

  get hiddenEventInverseOptions() {
    return this.args.hiddenEventInverseOptions ?? [];
  }

  get _canAdd() {
    return this.canAdd && !this.readOnly;
  }

  get addContainerFlexAlignment() {
    if (this.isAdding && this.args.variation !== 'compact') {
      return 'items-start';
    }
    return '';
  }

  get disableChangesDueToRedactedUser() {
    // if one of the conditions contain an attribute condition whilst user is redacted, we do not allow any changes
    return this.auth.redacted && this.deserializedFilters.some((f) => f.type === 'attribute');
  }

  @tracked newCondition: AbstractCondition | SerializedLegacyFilter | Record<string, unknown> | null = null;
  @tracked segments: {
    value: string;
    label: string;
    icon: string;
    model: ShortSegmentModel;
  }[] = [];

  @tracked isAdding = false;
  @tracked isCreatingSegment = false;

  operatorOptions = [
    { value: 'and', label: 'All' },
    { value: 'or', label: 'At least one' },
  ];

  @computed('args.conditions.[]', 'preferFilters')
  get conditions() {
    if (!isEmpty(this.args.conditions)) {
      return this.args.conditions!;
    }

    return this.preferFilters ? { and: [] } : [];
  }

  get deserializedFilters() {
    return deserializeFilters(this.conditions, false);
  }

  get disableForeignEvent() {
    // Only allow adding one foreign event filter
    return this.deserializedFilters.some((filter) => filter.type === 'foreign_event');
  }

  get markedConditions() {
    let markedConditions = this.deserializedFilters.map((condition) => {
      // Generate an "id" by sorting the condition keys, joining them into a
      // string and base64 encoding them.
      // This id is used in the #each loop to signal to Ember what needs to
      // re-render when the conditions array changes (we want existing
      // conditions to not rerender, so that they stay in edit mode).
      let id = btoa(
        encodeURIComponent(
          Object.keys(condition)
            .sort()
            .map((key) => `${key}:${(condition as any)[key]}`)
            .join('|'),
        ),
      );

      return {
        condition,
        id,
      };
    });

    return markedConditions;
  }

  @computed(
    'conditionTypes',
    'args.campaign.{isFormTriggered,isEventTriggered}',
    'args.{event,isCampaignReEntrant}',
    'disabled',
  )
  get typeButtons(): TypeButton[] | undefined {
    let { conditionTypes } = this;
    let { campaign, event } = this.args;

    // only show segments button if we're not using segments_negatable conditions
    if (conditionTypes?.includes(ConditionType.Segments) && conditionTypes?.includes(ConditionType.SegmentsNegatable)) {
      conditionTypes = conditionTypes.filter((type) => type !== ConditionType.Segments);
    }

    // only show event_attribute when the campaign is event, form, or webhook triggered
    if (!campaign?.isEventTriggered && !campaign?.isFormTriggered && !campaign?.isWebhookTriggered && conditionTypes) {
      conditionTypes = conditionTypes.filter((type) => type !== ConditionType.EventAttribute);
    }

    return conditionTypes
      ?.map((type) => CONDITION_TYPES[type as ButtonConditionType])
      .map((type) => {
        if (type.name === ConditionType.EventAttribute && campaign?.isWebhookTriggered) {
          type = { ...type, icon: 'webhook', addButtonLabel: 'Add webhook attribute condition' };
        }

        if (this.args.buttonLabelOverrides?.[type.name] != null) {
          type = { ...type, addButtonLabel: this.args.buttonLabelOverrides[type.name] };
        }

        if (type.name !== ConditionType.EventAttribute) {
          if (this.disabled && this.args.isCampaignReEntrant) {
            return {
              ...type,
              disabledTooltipContent:
                'Filters cannot be used with campaigns that have a frequency set. Please modify the trigger condition instead.',
            };
          }

          let isAttributeConditionForRedactedUser = type.name === ConditionType.Attribute && this.auth.redacted;
          if (isAttributeConditionForRedactedUser) {
            return {
              ...type,
              isDisabled: true,
              disabledTooltipContent:
                'You do not have permission to add attribute conditions. Contact the account admin for more information.',
            };
          }

          return type;
        }

        return {
          ...type,
          isDisabled: campaign?.isEventTriggered && isEmpty(event),
          disabledTooltipContent: 'Select an event on the trigger page first!',
        };
      });
  }

  // variation = 'compact'
  @computed('typeButtons')
  get typeDropdownOptions() {
    return (this.typeButtons ?? []).map(
      (typeButton) =>
        <DropdownOption>{
          label: typeButton.addButtonLabel,
          value: typeButton.name,
          disabled: Boolean(typeButton.isDisabled),
        },
    );
  }

  @computed('typeButtons')
  get changeTypeOptions() {
    return (this.typeButtons ?? []).map(
      (typeButton) =>
        <DropdownOption>{
          label: typeButton.addButtonLabel,
          value: typeButton.name,
          disabled: Boolean(typeButton.isDisabled),
        },
    );
  }

  @computed('preferFilters', 'conditions')
  get isFilters() {
    return this.preferFilters && !Array.isArray(this.conditions);
  }

  @computed('conditions')
  get operator(): FilterCondition {
    if (Array.isArray(this.conditions)) {
      return 'and';
    } else {
      return Object.keys(this.conditions)[0] as FilterCondition;
    }
  }

  set operator(value: FilterCondition) {
    let filters =
      value === 'and'
        ? this.deserializedFilters.reduce((agg, filter) => {
            if (isDuplicate(filter, agg)) {
              return agg;
            }

            return [...agg, filter];
          }, [])
        : this.deserializedFilters;
    this.args.onUpdate(serializeFilters(filters, value, this.isFilters));
  }

  get conditionViewerBackgroundVariant() {
    return this.args.conditionViewerBackgroundVariant ?? ConditionViewerBackgroundVariant.Dark;
  }

  constructor(owner: unknown, args: ConditionListArgs) {
    super(owner, args);
    this.findAndSetSegments();

    if (this.startInCreateState && this.args.initialCreateConditionType != null) {
      this.add(this.args.initialCreateConditionType);
    }
  }

  @action
  create(condition: SerializedLegacyFilter) {
    let { deserializedFilters: conditions } = this;
    let clonedConditions = [...conditions];

    if (isComplete(condition) && !isDuplicate(condition, conditions)) {
      clonedConditions.pushObject(condition);
    }

    this.isAdding = false;
    this.newCondition = null;

    return this.args.onUpdate(serializeFilters(clonedConditions, this.operator, this.isFilters));
  }

  @action
  delete(index: number) {
    let { deserializedFilters: conditions } = this;
    let newConditions = [...conditions.slice(0, index), ...conditions.slice(index + 1)];

    return this.args.onUpdate(serializeFilters(newConditions, this.operator, this.isFilters));
  }

  @action
  update(condition: SerializedLegacyFilter, index: number) {
    if (!isComplete(condition)) {
      return;
    }

    let { deserializedFilters: conditions } = this;
    let otherConditions = conditions.slice(0, index).concat(conditions.slice(index + 1));

    if (isDuplicate(condition, otherConditions)) {
      return this.args.onUpdate(serializeFilters([...conditions], this.operator, this.isFilters));
    }

    let newConditions = [...conditions.slice(0, index), condition, ...conditions.slice(index + 1)];

    return this.args.onUpdate(serializeFilters(newConditions, this.operator, this.isFilters));
  }

  @action
  add(type: SerializedLegacyFilterType) {
    // Record type only to handle initial value, which is always overwritten:
    // (code path kept to be extra safe: not sure it's ever entered)
    let newCondition: AbstractCondition | Record<string, unknown> = { type };

    if (type === 'attribute') {
      newCondition = deserializeAttributeFilter({ operator: 'eq', event: this.args.event, field: '' });
    } else if (([ConditionType.RelationshipAttribute, ConditionType.ObjectAttribute] as string[]).includes(type)) {
      let objectTypes = this.objectTypes.enabledObjectTypes;
      let objectTypeId = objectTypes.length > 0 ? Number(objectTypes[0].id) : null;
      let objectAttributeSource = ObjectAttributeSource.ObjectAttribute;

      if (this.args.objectTypeId != null) {
        objectTypeId = Number(this.args.objectTypeId);
      }

      if (type === ConditionType.RelationshipAttribute) {
        objectAttributeSource = ObjectAttributeSource.RelationshipAttribute;
      }

      newCondition = deserializeAttributeFilter({
        operator: 'eq',
        event: this.args.event,
        field: '',
        type,
        type_id: objectTypeId ?? undefined,
        objectAttributeSource,
      });
    } else if (type === 'event_attribute') {
      newCondition = deserializeAttributeFilter({
        operator: 'eq',
        event: this.args.event,
        type,
        field: '',
      });
    } else if (type === 'collection_attribute') {
      newCondition = deserializeAttributeFilter({
        operator: 'eq',

        // @ts-ignore: this shouldn't work? It's typed as string, but this is an array of strings
        collectionSchema: this.args.collection?.get('schema'),

        event: this.args.event,
        type,
        field: '',
      });
    } else if (type === 'message') {
      // @ts-ignore: this shouldn't work? "condition" is nowhere in any condition types
      // keeping it in case it's actually important
      newCondition.condition = ActionCondition.create({ type: 'email', isForeignEvent: this.allowForeignEvent });
    } else if (type === 'event') {
      newCondition = EventCondition.create({
        isForeignEvent: this.allowForeignEvent && !this.disableForeignEvent,
      });
    } else if (type === 'relationship') {
      let objectTypes = this.objectTypes.enabledObjectTypes;
      let objectTypeId = objectTypes.length > 0 ? objectTypes[0].id : null;
      // prefill with first, as it's the first selection in dropdown anyways

      if (this.args.objectTypeId != null) {
        objectTypeId = String(this.args.objectTypeId);
      }
      newCondition = RelationshipCondition.create({ typeId: String(objectTypeId) });
    } else if (type === 'device_attribute') {
      newCondition = DeviceAttributeCondition.create({ name: '', operator: 'eq', value: '' });
    }

    this.isAdding = true;
    this.newCondition = newCondition;
  }

  @action
  cancel() {
    this.isAdding = false;
    this.newCondition = null;
  }

  get newConditionLabel() {
    if (this.isAdding && this.newCondition !== null) {
      return this.typeButtons?.find((opt) => opt.name === this.newCondition?.type)?.addButtonLabel ?? '';
    }
    return '';
  }

  @action
  toggleCreatingSegment() {
    this.isCreatingSegment = !this.isCreatingSegment;

    if (this.isCreatingSegment) {
      this.args.onToggleCreateSegment?.();
    }
  }

  @action
  didCreateSegment(segment: SegmentModel) {
    let condition: SerializedLegacySegmentNegatableFilter = {
      type: 'segments_negatable',
      segments: [segment.id],
      negate: false,
    };

    this.args.onUpdate(serializeFilters([...this.deserializedFilters, condition], this.operator, this.isFilters));
    this.findAndSetSegments();

    this.isCreatingSegment = false;
    this.isAdding = false;
    this.args.onNewSegmentCreated?.();
  }

  private findAndSetSegments() {
    let segments = this.store.peekAll('short-segment').map((segment) => ({
      value: segment.id,
      label: segment.name,
      icon: segment.icon,
      model: segment,
    }));

    this.segments = segments;
  }
}
